import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth, SignInButton } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { formatInUserTime } from '../utils/date';
import QRCode from 'qrcode';

const CUSTOMER_CONTEXT_KEY = 'gng_customerContext';

/**
 * Order success / receipt page at /order/:orderId/success.
 * Displays order summary and QR code for pickup.
 * Sets customer context so Landing won't redirect to owner onboarding.
 */
export default function OrderSuccess() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const sessionId = searchParams.get('session_id') || '';
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem(CUSTOMER_CONTEXT_KEY, '1');
  }, []);

  const successToken = searchParams.get('token') || '';

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Token-based fetch (no auth) — survives Stripe redirect when Clerk session is lost
    const fetchOrderByToken = async () => {
      if (!successToken) return null;
      try {
        const res = await api(`/api/orders/${orderId}/by-token?token=${encodeURIComponent(successToken)}`);
        if (res.ok) return await res.json();
        return null;
      } catch {
        return null;
      }
    };

    const verifyPaymentByToken = async (sid) => {
      if (!successToken) return null;
      try {
        const res = await api(`/api/orders/${orderId}/verify-payment-by-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: successToken, sessionId: sid || sessionId }),
        });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    };

    // Auth-based fetch (requires Clerk session)
    const fetchOrderByAuth = async () => {
      try {
        const getTokenFresh = () => getToken({ skipCache: true });
        const res = await api(`/api/orders/${orderId}`, { getToken: getTokenFresh });
        if (res.ok) return { ok: true, data: await res.json(), status: res.status };
        const body = await res.json().catch(() => ({}));
        return { ok: false, status: res.status, error: body.error };
      } catch {
        return { ok: false, status: 0 };
      }
    };

    const verifyPaymentByAuth = async (sid) => {
      try {
        const getTokenFresh = () => getToken({ skipCache: true });
        const res = await api(`/api/orders/${orderId}/verify-payment`, {
          getToken: getTokenFresh,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: sid || sessionId }),
        });
        if (!res.ok) return null;
        return await res.json();
      } catch {
        return null;
      }
    };

    const generateQr = async (payload) => {
      try {
        const url = await QRCode.toDataURL(payload, { width: 280, margin: 2 });
        if (!cancelled) setQrDataUrl(url);
      } catch (e) {
        console.warn('QR generation failed', e);
      }
    };

    (async () => {
      setLoading(true);
      setError(null);

      // Path 1: Token in URL (from Stripe redirect) — no auth needed, survives session loss
      if (successToken) {
        let data = await fetchOrderByToken();
        if (cancelled) return;
        if (data && !data.receiptQrPayload) {
          const verified = await verifyPaymentByToken(sessionId);
          if (cancelled) return;
          if (verified) data = verified;
        }
        if (data) {
          setOrder(data);
          setLoading(false);
          if (data.receiptQrPayload) await generateQr(data.receiptQrPayload);
          else {
            for (let i = 0; i < 15 && !cancelled; i++) {
              await new Promise((r) => setTimeout(r, 2000));
              const fresh = await fetchOrderByToken();
              if (cancelled) return;
              if (fresh?.receiptQrPayload) {
                setOrder(fresh);
                await generateQr(fresh.receiptQrPayload);
                break;
              }
              if (fresh) setOrder(fresh);
            }
          }
          return;
        }
        // Token failed (expired/invalid) — fall through to auth if signed in
        if (!isLoaded || !isSignedIn) {
          setError('Link expired. Sign in to view your order.');
          setLoading(false);
          return;
        }
      }

      // Path 2: No token — use auth (requires Clerk session)
      if (!isLoaded) return;
      if (!isSignedIn) {
        setError('Your session was lost. Please sign in to view your order.');
        setLoading(false);
        return;
      }

      await new Promise((r) => setTimeout(r, 500));
      let result = null;
      for (let attempt = 0; attempt < 8 && !cancelled; attempt++) {
        result = await fetchOrderByAuth();
        if (result.ok) break;
        if (result.status === 403 || result.status === 404) break;
        await new Promise((r) => setTimeout(r, 1500));
      }

      if (cancelled) return;
      if (!result?.ok) {
        if (result?.status === 403) setError('This order belongs to a different account. Sign in with the account you used to place the order.');
        else if (result?.status === 404) setError('Order not found. If you completed checkout on the test site, open this link there (or vice versa).');
        else if (result?.status === 401) setError('Sign-in session expired. Please sign in again and visit your Dashboard to view this order.');
        else setError('Order not found. Please try again in a moment.');
        setLoading(false);
        return;
      }

      const data = result.data;
      setOrder(data);
      setLoading(false);

      if (data.receiptQrPayload) {
        await generateQr(data.receiptQrPayload);
        return;
      }

      if (!cancelled) {
        const verified = await verifyPaymentByAuth(sessionId);
        if (cancelled) return;
        if (verified?.receiptQrPayload) {
          setOrder(verified);
          await generateQr(verified.receiptQrPayload);
          return;
        }
        if (verified) setOrder(verified);
      }

      for (let i = 0; i < 15 && !cancelled; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const freshResult = await fetchOrderByAuth();
        if (cancelled) return;
        if (freshResult?.ok && freshResult.data?.receiptQrPayload) {
          setOrder(freshResult.data);
          await generateQr(freshResult.data.receiptQrPayload);
          return;
        }
      }

      if (!cancelled) {
        const verified = await verifyPaymentByAuth(sessionId);
        if (verified?.receiptQrPayload) {
          setOrder(verified);
          await generateQr(verified.receiptQrPayload);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [orderId, getToken, sessionId, isLoaded, isSignedIn, successToken]);

  const handlePrintReceipt = () => {
    window.print();
  };

  if (loading && !order) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-slate-600 dark:text-slate-400">Loading receipt…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto py-12 px-4 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
        {(error?.includes('Sign-in session') || error?.includes('session was lost') || error?.includes('Link expired') || error?.includes('Token expired')) ? (
          <SignInButton mode="redirect" forceRedirectUrl={window.location.href}>
            <button className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium">
              Sign in again
            </button>
          </SignInButton>
        ) : (
          <Link
            to="/profile"
            className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
          >
            Back to Dashboard
          </Link>
        )}
      </div>
    );
  }

  if (!order) {
    return null;
  }

  return (
    <div className="max-w-md mx-auto py-12 px-4 print:py-4">
      <Link
        to="/profile"
        className="no-print mb-4 inline-block text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
      >
        ← Return to Dashboard
      </Link>
      <div ref={receiptRef} className="receipt-print-area rounded-2xl bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 p-8 print:border print:shadow-none">
        <h1 className="text-2xl font-semibold text-green-800 dark:text-green-200 mb-2">
          Payment complete
        </h1>
        <p className="text-green-700 dark:text-green-300 mb-6">
          Thank you for your order at <strong>{order.businessName}</strong>.
        </p>

        {order.pickupAddress?.trim() && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.pickupAddress)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-4 mb-6 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <span className="flex-shrink-0 w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Pickup location</p>
              <p className="text-sm text-slate-600 dark:text-slate-400 truncate">{order.pickupAddress}</p>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}

        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 mb-6 space-y-2">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Order #{order.id} · {formatInUserTime(order.createdAt)}
          </p>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Status: {order.status}
          </p>
          {order.items?.map((item) => (
            <div
              key={item.id}
              className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0"
            >
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {item.name} × {item.quantity}
                </p>
                {(() => {
                  let mods = null;
                  if (item.modifiersJson) {
                    try { mods = JSON.parse(item.modifiersJson); } catch { /* ignore */ }
                  }
                  return mods?.length > 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {mods.map((m) => `${m.groupName}: ${m.optionName}`).join(' · ')}
                    </p>
                  ) : null;
                })()}
                {(() => {
                  let addons = null;
                  if (item.addonsJson) {
                    try { addons = JSON.parse(item.addonsJson); } catch { /* ignore */ }
                  }
                  return addons?.length > 0 ? (
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      + {addons.map((a) => a.name).join(', ')}
                    </p>
                  ) : null;
                })()}
                {item.notes && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 italic">"{item.notes}"</p>
                )}
              </div>
              <p className="font-medium text-slate-900 dark:text-slate-100">
                ${((item.unitPriceCents * item.quantity) / 100).toFixed(2)}
              </p>
            </div>
          ))}
          <div className="flex justify-between pt-2 text-lg font-semibold">
            <span className="text-slate-900 dark:text-slate-100">Total</span>
            <span className="text-slate-900 dark:text-slate-100">
              ${(order.totalCents / 100).toFixed(2)}
            </span>
          </div>
        </div>

        {qrDataUrl && (
          <div className="flex flex-col items-center mb-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-1">
              Show this QR to the owner at pickup
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
              The owner will scan it to mark your order as picked up
            </p>
            <div className="bg-white p-4 rounded-xl inline-block shadow-sm">
              <img src={qrDataUrl} alt="Order pickup QR - show to owner" className="w-[200px] h-[200px] sm:w-[280px] sm:h-[280px]" />
            </div>
          </div>
        )}

        {!qrDataUrl && ['pending', 'paid'].includes(order.status) && (
          <div className="flex items-center justify-center gap-3 p-4 mb-6 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <svg className="animate-spin h-5 w-5 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Generating your pickup QR code... this may take a moment.
            </p>
          </div>
        )}

        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          You can also track your order status from the Dashboard.
        </p>

        <div className="no-print flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handlePrintReceipt}
            className="flex-1 py-3 rounded-lg bg-slate-700 hover:bg-slate-800 text-white font-medium"
          >
            Download / Print receipt
          </button>
          <Link
            to="/profile"
            className="flex-1 py-3 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-center"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          .receipt-print-area, .receipt-print-area * { visibility: visible; }
          .receipt-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
}
